import type {
  PatchAdminV1OrgProductsIdVariantsVariantIdBody,
  PostAdminV1OrgProductsIdVariantsBody,
  Product,
  Variant,
} from '@nucleus-commerce/api-client/admin';

import type {
  NewVariantFormInput,
  NewVariantFormOutput,
  VariantFormInput,
  VariantFormOutput,
} from '../schemas/variant-form-schema';

import { fromWeight, toWeight } from './weight-mappers';

const text = (v: number | null): string => (v === null ? '' : String(v));
const num = (v: string): number | null => (v === '' ? null : Number(v));

export function fromVariant(variant: Variant): VariantFormInput {
  return {
    sku: variant.sku ?? '',
    barcode: variant.barcode ?? '',
    price: variant.price,
    compareAt: variant.compareAt,
    cost: variant.cost,
    weight: fromWeight(variant.weight),
    lengthMm: text(variant.dimensions?.lengthMm ?? null),
    widthMm: text(variant.dimensions?.widthMm ?? null),
    heightMm: text(variant.dimensions?.heightMm ?? null),
    requiresShipping: variant.requiresShipping,
    taxable: variant.taxable,
    taxCode: variant.taxCode ?? '',
    trackInventory: variant.trackInventory,
    allowBackorder: variant.allowBackorder,
    fulfillmentMode: variant.fulfillmentMode ?? '',
    leadTimeDaysMin: text(variant.leadTimeDaysMin),
    leadTimeDaysMax: text(variant.leadTimeDaysMax),
    status: variant.status,
  };
}

/** Every field the sheet shows is sent, so a cleared field clears on the server. */
export function toVariantUpdate(
  values: VariantFormOutput,
): PatchAdminV1OrgProductsIdVariantsVariantIdBody {
  const dimensions =
    values.lengthMm !== '' && values.widthMm !== '' && values.heightMm !== ''
      ? {
          lengthMm: Number(values.lengthMm),
          widthMm: Number(values.widthMm),
          heightMm: Number(values.heightMm),
        }
      : null;
  return {
    sku: values.sku || null,
    barcode: values.barcode || null,
    price: values.price,
    compareAt: values.compareAt,
    cost: values.cost,
    weight: toWeight(values.weight),
    dimensions,
    requiresShipping: values.requiresShipping,
    taxable: values.taxable,
    taxCode: values.taxCode || null,
    trackInventory: values.trackInventory,
    allowBackorder: values.allowBackorder,
    fulfillmentMode: values.fulfillmentMode || null,
    leadTimeDaysMin: num(values.leadTimeDaysMin),
    leadTimeDaysMax: num(values.leadTimeDaysMax),
    status: values.status,
  };
}

export function emptyNewVariantForm(
  product: Pick<Product, 'options'>,
  defaultPrice: { amount: number; currency: string } | null,
): NewVariantFormInput {
  return {
    optionValues: Object.fromEntries(product.options.map((axis) => [axis.key, ''])),
    sku: '',
    price: defaultPrice,
    compareAt: null,
  };
}

/** Option-less products send `optionValues: {}`, which the backend accepts as the one default variant. */
export function toVariantCreate(
  values: NewVariantFormOutput,
): PostAdminV1OrgProductsIdVariantsBody {
  return {
    optionValues: values.optionValues,
    ...(values.sku ? { sku: values.sku } : {}),
    price: values.price,
    ...(values.compareAt ? { compareAt: values.compareAt } : {}),
  };
}

/** The variant table's form: one row of sheet fields per variant, keyed by variant id. */
export interface VariantRowsInput {
  rows: Record<string, VariantFormInput>;
}

export function fromVariants(variants: readonly Variant[]): VariantRowsInput {
  return {
    rows: Object.fromEntries(variants.map((variant) => [variant.id, fromVariant(variant)])),
  };
}

/** A cell of the variant table's form. */
export const cellPath = <F extends keyof VariantFormInput>(
  id: string,
  field: F,
): `rows.${string}.${F}` => `rows.${id}.${field}`;

type VariantBodyKey = keyof PatchAdminV1OrgProductsIdVariantsVariantIdBody;

/** Which body key a form field writes; the three sides write one `dimensions`. */
const BODY_KEY: Record<keyof VariantFormInput, VariantBodyKey> = {
  sku: 'sku',
  barcode: 'barcode',
  price: 'price',
  compareAt: 'compareAt',
  cost: 'cost',
  weight: 'weight',
  lengthMm: 'dimensions',
  widthMm: 'dimensions',
  heightMm: 'dimensions',
  requiresShipping: 'requiresShipping',
  taxable: 'taxable',
  taxCode: 'taxCode',
  trackInventory: 'trackInventory',
  allowBackorder: 'allowBackorder',
  fulfillmentMode: 'fulfillmentMode',
  leadTimeDaysMin: 'leadTimeDaysMin',
  leadTimeDaysMax: 'leadTimeDaysMax',
  status: 'status',
};

export type VariantsUpdateItem = PatchAdminV1OrgProductsIdVariantsVariantIdBody & { id: string };

/**
 * The rows the person changed, each with only its changed fields, in `order`. Sending untouched
 * fields would rewrite them, and an unchanged generated SKU sent back is still not "typed".
 */
export function toVariantsUpdate(
  values: Record<string, VariantFormOutput>,
  dirtyRows: Partial<Record<string, Partial<Record<keyof VariantFormInput, unknown>>>>,
  order: readonly string[],
): VariantsUpdateItem[] {
  return order.flatMap((id) => {
    const dirty = dirtyRows[id];
    const row = values[id];
    if (!dirty || !row) return [];
    const full = toVariantUpdate(row);
    const keys = new Set(
      (Object.keys(dirty) as Array<keyof VariantFormInput>)
        .filter((field) => Boolean(dirty[field]))
        .map((field) => BODY_KEY[field]),
    );
    if (keys.size === 0) return [];
    const body = Object.fromEntries([...keys].map((key) => [key, full[key]]));
    return [{ id, ...body }];
  });
}

/** Form field a server error on a body key belongs to. */
const FORM_FIELD: Partial<Record<string, keyof VariantFormInput>> = {
  ...Object.fromEntries(
    (Object.keys(BODY_KEY) as Array<keyof VariantFormInput>).map((field) => [field, field]),
  ),
  dimensions: 'heightMm',
  weightGrams: 'weight',
};

/**
 * `body.items.3.price.currency` → `rows.<id of item 3>.price`; null when the error is about the
 * item as a whole (its id) or not about an item.
 */
export function variantsErrorPath(
  field: string,
  items: ReadonlyArray<{ id: string }>,
): `rows.${string}.${keyof VariantFormInput}` | null {
  const match = /^body\.items\.(\d+)\.([A-Za-z]+)/.exec(field);
  const item = match ? items[Number(match[1])] : undefined;
  const formField = match?.[2] ? FORM_FIELD[match[2]] : undefined;
  return item && formField ? `rows.${item.id}.${formField}` : null;
}
